import { Link, router } from "@inertiajs/react"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { VoteButton } from "@/components/vote-button"
import { MiniWaveform } from "@/components/mini-waveform"
import { PlayButton } from "@/components/play-button"
import { useSamplePlayer } from "@/hooks/use-sample-player"
import { displayName } from "@/lib/utils"
import type Sample from "@/types/generated/Sample"

type Props = {
  sample: Sample
  showUser?: boolean
}

export function SampleCard({ sample, showUser = true }: Props) {
  const {
    isPlaying,
    isLoading,
    progress,
    canPlay,
    handlePlayPause,
    handleSeek,
  } = useSamplePlayer(sample)

  const navigateToSample = () => {
    router.visit(`/samples/${sample.id}`)
  }

  return (
    <Card
      className="p-4 hover:bg-card/80 transition-colors border-flicker-hover cursor-pointer"
      onClick={navigateToSample}
    >
        <div className="flex items-start gap-3">
          <PlayButton
            isPlaying={isPlaying}
            isLoading={isLoading}
            disabled={!canPlay}
            onClick={handlePlayPause}
            size="sm"
          />

          {showUser && (
            <Link
              href={`/users/${sample.user.id}`}
              onClick={(e) => e.stopPropagation()}
              className="shrink-0"
            >
              <Avatar className="h-8 w-8 hover:ring-2 hover:ring-primary transition-all">
                <AvatarImage src={sample.user.avatar_url ?? undefined} />
                <AvatarFallback>{displayName(sample.user)[0]}</AvatarFallback>
              </Avatar>
            </Link>
          )}

          <div className="flex-1 min-w-0">
            <h3 className="font-medium truncate">{sample.title}</h3>
            {showUser && (
              <Link
                href={`/users/${sample.user.id}`}
                onClick={(e) => e.stopPropagation()}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {displayName(sample.user)}
              </Link>
            )}

            {sample.waveform && (
              <div className="my-2">
                <MiniWaveform
                  peaks={sample.waveform}
                  progress={progress}
                  isPlaying={isPlaying}
                  onClick={canPlay ? handleSeek : undefined}
                />
              </div>
            )}

            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {sample.bpm && (
                <Badge variant="secondary" className="font-mono text-xs">
                  {sample.bpm} BPM
                </Badge>
              )}
              {sample.key && (
                <Badge variant="secondary" className="font-mono text-xs">
                  {sample.key}
                </Badge>
              )}
              {sample.tags.slice(0, 3).map((tag) => (
                <Badge key={tag.id} variant="outline" className="text-xs">
                  {tag.name}
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex flex-col items-end gap-1">
            <VoteButton
              sampleId={sample.id}
              voted={sample.voted}
              votesCount={sample.votes_count}
              size="sm"
            />
            <span className="text-xs text-muted-foreground font-mono">
              {sample.downloads_count} DLs
            </span>
          </div>
        </div>
    </Card>
  )
}
